import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit, Loader2, Search, Users, Link, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

interface WarmupGroup {
  id: string;
  name: string;
  description: string | null;
  invite_code: string;
  group_jid: string | null;
  is_active: boolean;
  created_at: string;
}

export default function DashboardGroups() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<WarmupGroup | null>(null);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [groupActive, setGroupActive] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Validation state
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    name?: string;
    size?: number;
    group_jid?: string;
    error?: string;
  } | null>(null);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["user_warmup_groups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_groups")
        .select("*")
        .eq("is_system", false)
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as WarmupGroup[];
    },
    enabled: !!user,
  });

  // Fetch memberships with instance info for all user groups
  const { data: memberships = [] } = useQuery({
    queryKey: ["group_memberships_with_instances", groups.map(g => g.id)],
    queryFn: async () => {
      if (groups.length === 0) return [];
      const { data, error } = await supabase
        .from("instance_group_memberships")
        .select("warmup_group_id, status, instances(id, phone_number, nickname)")
        .in("warmup_group_id", groups.map(g => g.id))
        .in("status", ["joined", "pending"]);
      if (error) throw error;
      return data as Array<{
        warmup_group_id: string;
        status: string;
        instances: { id: string; phone_number: string; nickname: string | null } | null;
      }>;
    },
    enabled: groups.length > 0,
  });

  const getMembersForGroup = (groupId: string) => {
    return memberships.filter(m => m.warmup_group_id === groupId && m.instances);
  };

  const extractInviteCode = (input: string): string => {
    const trimmed = input.trim();
    // Extract from full URL
    const match = trimmed.match(/chat\.whatsapp\.com\/([A-Za-z0-9_-]+)/);
    if (match) return match[1];
    // Already just the code
    return trimmed;
  };

  const handleValidate = async () => {
    const code = extractInviteCode(inviteLink);
    if (!code) {
      toast({ title: "Cole o link do grupo", variant: "destructive" });
      return;
    }
    setValidating(true);
    setValidationResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("validate-group", {
        body: { invite_code: code },
      });
      if (error) throw error;
      if (data?.valid) {
        if (data.size >= 1024) {
          setValidationResult({ valid: false, error: `Grupo lotado (${data.size}/1024 membros). Não é possível adicionar.` });
        } else {
          setValidationResult(data);
          if (!groupName && data.name) setGroupName(data.name);
        }
      } else {
        setValidationResult({ valid: false, error: data?.error || "Grupo inválido" });
      }
    } catch (e: any) {
      setValidationResult({ valid: false, error: e.message || "Erro ao validar" });
    } finally {
      setValidating(false);
    }
  };

  const saveGroup = useMutation({
    mutationFn: async () => {
      const code = extractInviteCode(inviteLink);
      if (!code) throw new Error("Link de convite inválido");

      const payload = {
        name: groupName,
        description: groupDescription || null,
        invite_code: code,
        is_active: groupActive,
        group_jid: validationResult?.group_jid || null,
        is_system: false,
        user_id: user!.id,
        category: "custom",
      };

      if (editingGroup) {
        const { error } = await supabase.from("warmup_groups").update(payload).eq("id", editingGroup.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("warmup_groups").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_warmup_groups"] });
      setDialogOpen(false);
      resetForm();
      toast({ title: editingGroup ? "Grupo atualizado" : "Grupo adicionado" });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("warmup_groups").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_warmup_groups"] });
      toast({ title: "Grupo removido" });
    },
  });

  const resetForm = () => {
    setEditingGroup(null);
    setGroupName("");
    setGroupDescription("");
    setInviteLink("");
    setGroupActive(true);
    setValidationResult(null);
  };

  const openEdit = (g: WarmupGroup) => {
    setEditingGroup(g);
    setGroupName(g.name);
    setGroupDescription(g.description || "");
    setInviteLink(`https://chat.whatsapp.com/${g.invite_code}`);
    setGroupActive(g.is_active);
    setValidationResult(null);
    setDialogOpen(true);
  };

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.invite_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Grupos de Aquecimento</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Cadastre seus próprios grupos para participar do aquecimento dos seus números.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar grupo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-secondary/40 border-warmz-glass-border"
          />
        </div>
        <Button className="gap-2 glow-orange-sm" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="h-4 w-4" /> Novo Grupo
        </Button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead>Nome</TableHead>
                     <TableHead>Números</TableHead>
                     <TableHead>Link</TableHead>
                     <TableHead>Ativo</TableHead>
                     <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                       <TableRow key={i}>
                         {Array.from({ length: 5 }).map((_, j) => (
                           <TableCell key={j}><Skeleton className="h-5 w-20" /></TableCell>
                         ))}
                       </TableRow>
                     ))
                  ) : filteredGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                        {groups.length === 0 ? "Nenhum grupo cadastrado ainda." : "Nenhum resultado encontrado."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGroups.map((g) => (
                      <TableRow key={g.id} className="border-warmz-glass-border/20">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-accent shrink-0" />
                            <div>
                              <p className="font-medium text-foreground text-sm">{g.name}</p>
                              {g.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{g.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                           {(() => {
                             const members = getMembersForGroup(g.id);
                             if (members.length === 0) {
                               return <span className="text-xs text-muted-foreground">Nenhum</span>;
                             }
                             return (
                               <div className="flex flex-wrap gap-1">
                                 {members.map((m) => (
                                   <Badge key={m.instances!.id} variant={m.status === "joined" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
                                     {m.instances!.nickname || m.instances!.phone_number}
                                   </Badge>
                                 ))}
                               </div>
                             );
                           })()}
                         </TableCell>
                         <TableCell>
                           <a
                             href={`https://chat.whatsapp.com/${g.invite_code}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-xs text-accent hover:underline font-mono"
                           >
                             ...{g.invite_code.slice(-8)}
                           </a>
                         </TableCell>
                        <TableCell>
                          <Badge variant={g.is_active ? "default" : "secondary"} className="text-xs">
                            {g.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => deleteGroup.mutate(g.id)}
                              disabled={deleteGroup.isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialog: Create/Edit Group */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Editar Grupo" : "Adicionar Grupo"}</DialogTitle>
            <DialogDescription>
              Cole o link de convite do grupo do WhatsApp para validar e cadastrar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Invite Link + Validate */}
            <div className="space-y-1.5">
              <Label className="text-xs">Link de Convite</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  onChange={(e) => { setInviteLink(e.target.value); setValidationResult(null); }}
                  placeholder="https://chat.whatsapp.com/..."
                  className="bg-secondary/40 border-warmz-glass-border flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={handleValidate}
                  disabled={validating || !inviteLink.trim()}
                >
                  {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link className="h-3.5 w-3.5" />}
                  Validar
                </Button>
              </div>

              {/* Validation result */}
              {validationResult && (
                <div className={`flex items-start gap-2 p-2.5 rounded-lg text-sm ${
                  validationResult.valid
                    ? "bg-accent/10 text-accent border border-accent/20"
                    : "bg-destructive/10 text-destructive border border-destructive/20"
                }`}>
                  {validationResult.valid ? (
                    <>
                      <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">{validationResult.name}</p>
                        <p className="text-xs opacity-80">{validationResult.size} participantes</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <p>{validationResult.error}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs">Nome do Grupo</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ex: Grupo de Vendas"
                className="bg-secondary/40 border-warmz-glass-border"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs">Descrição (opcional)</Label>
              <Input
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Ex: Grupo de aquecimento do setor comercial"
                className="bg-secondary/40 border-warmz-glass-border"
              />
            </div>

            {/* Active */}
            <div className="flex items-center justify-between">
              <Label className="text-xs">Ativo</Label>
              <Switch checked={groupActive} onCheckedChange={setGroupActive} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => saveGroup.mutate()}
              disabled={saveGroup.isPending || !groupName.trim() || !inviteLink.trim()}
              className="gap-1.5"
            >
              {saveGroup.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editingGroup ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
